// The control for every row in Settings, by row id (shared/settings-index.js). main.js lays the rows
// out from the index; this file only says what goes on the right of each one.
import { toggle, select, groupedSelect, color, fontSelect, textInput, button, note } from "./ui.js";
import { get, set, setMany, subscribe } from "../shared/settings-store.js";
import { ACCENT_PRESETS, SHORTCUTS, CLOAK_PRESETS, MUSIC_PRESETS, BUNDLES, BUNDLE_KEYS, DEFAULTS } from "../shared/settings-schema.js";
import { FONTS, loadPreviewFonts } from "../shared/fonts.js";
import { themeGroups } from "./picker-lists.js";
import { EXTENSION_BY_KEY, EXTENSION_MAP } from "../shared/extensions.js";
import { canPin, isPinned, pinPatch, movePatch, toolbarExts } from "../shared/ext-pins.js";
import { voiceList, RATES } from "../shared/read-core.js";
import { clearStats } from "../shared/activity.js";
import { clearStreak } from "../shared/streak-live.js";
import { remove as storeRemove } from "../shared/store.js";
import { clearMilestones } from "../app/quests.js";
import { looksControl } from "./sections/bundles.js";
import { showable, pinWaiting } from "../shared/bumper-pick.js";
import { brandQuiet } from "../shared/brand-quiet.js";
import { wallpaperControls } from "./sections/wallpaper.js";
import { bellControls } from "./sections/bell.js";
import { musicControls } from "./sections/music.js";
import { linuxControls } from "./sections/linux.js";
import { proxyControls } from "./sections/proxy.js";
import { privacyInfo, privacyActions, backupActions, troubleActions } from "./sections/privacy.js";
import { accountBlock } from "./sections/account.js";
import { dataHere } from "./sections/data-here.js";
import { carryRow } from "./sections/carry-row.js";
import { supporterBlock } from "./sections/supporter.js";
import { betaEntryControl } from "./sections/beta.js";
import { openShareSheet } from "../shared/share-gate.js";

const el = (tag, cls, text) => { const e = document.createElement(tag); if (cls) e.className = cls; if (text != null) e.textContent = text; return e; };

// Summer break: a month and a day for each end ("MM-DD").
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function summerControl() {
	const wrap = el("div", "rv-summer");
	const part = (key, label) => {
		const g = el("span", "rv-summer-part");
		const m = el("select", "rv-select"), d = el("select", "rv-select");
		m.setAttribute("aria-label", label + " month");
		d.setAttribute("aria-label", label + " day");
		MONTHS.forEach((n, i) => { const o = el("option", "", n); o.value = String(i + 1).padStart(2, "0"); m.append(o); });
		for (let i = 1; i <= 31; i++) { const o = el("option", "", String(i)); o.value = String(i).padStart(2, "0"); d.append(o); }
		const sync = () => { const v = /^\d{2}-\d{2}$/.test(get(key)) ? get(key) : DEFAULTS[key]; m.value = v.slice(0, 2); d.value = v.slice(3); };
		const save = () => set(key, m.value + "-" + d.value);
		m.addEventListener("change", save);
		d.addEventListener("change", save);
		subscribe(sync);
		sync();
		g.append(el("span", "rv-summer-l", label), m, d);
		return g;
	};
	wrap.append(part("streakSummerFrom", "From"), part("streakSummerTo", "To"));
	return wrap;
}

function fontControl() {
	// The list previews each font in its own face, which needs every web font: wait until the
	// student reaches the control.
	const f = fontSelect("font", FONTS);
	f.addEventListener("pointerenter", loadPreviewFonts, { once: true });
	f.addEventListener("focus", loadPreviewFonts, { once: true });
	return f;
}

// Which start page shortcuts show. The same switches as the start page's "Remove from start page"
// and All shortcuts' "Add back". Folded into a disclosure: fifteen switches are a lot to scroll past.
function shortcutsControl() {
	const d = el("details", "rv-disclose");
	const sum = el("summary", "");
	const count = () => SHORTCUTS.filter((s) => get(s.flag) !== false).length;
	const paint = () => { sum.textContent = "Choose shortcuts (" + count() + " of " + SHORTCUTS.length + " on)"; };
	paint();
	subscribe(paint);
	const wrap = el("div", "rv-multi");
	wrap.setAttribute("role", "group");
	wrap.setAttribute("aria-label", "On your start page");
	SHORTCUTS.forEach((sc) => {
		const item = el("div", "rv-multi-item");
		const span = el("span", "", sc.label);
		span.id = "rv-sc-" + sc.flag;
		const sw = toggle(sc.flag);
		sw.setAttribute("aria-labelledby", span.id); // its own name, not the row's
		item.append(span, sw);
		wrap.appendChild(item);
	});
	d.append(sum, wrap);
	return d;
}

// The extension buttons on the toolbar, in order, with Move up / Move down / Remove for people who
// prefer a list to the buttons' own menu.
function extButtonsControl() {
	const wrap = el("div", "rv-pinlist");
	const render = () => {
		const keep = wrap.contains(document.activeElement) ? document.activeElement.dataset.k : "";
		wrap.textContent = "";
		const ids = toolbarExts(get);
		if (!ids.length) { wrap.append(note("No extension buttons yet. Turn on a page tool in Extensions and its button shows up here.")); return; }
		const ol = el("ol", "rv-pinlist-ol");
		ids.forEach((id, i) => {
			const x = EXTENSION_MAP[id];
			const li = el("li", "rv-pinlist-row");
			const ic = el("span", "rv-pinlist-ic");
			ic.innerHTML = x.icon;
			const name = el("span", "rv-pinlist-name", x.label);
			const up = button("Move up", () => { const p = movePatch(id, -1, get); if (p) setMany(p); });
			up.dataset.k = "up" + id;
			up.disabled = i === 0;
			up.setAttribute("aria-label", "Move " + x.label + " up");
			const down = button("Move down", () => { const p = movePatch(id, 1, get); if (p) setMany(p); });
			down.dataset.k = "down" + id;
			down.disabled = i === ids.length - 1;
			down.setAttribute("aria-label", "Move " + x.label + " down");
			const off = button("Remove", () => setMany(pinPatch(id, false, get)));
			off.dataset.k = "off" + id;
			off.setAttribute("aria-label", "Remove " + x.label + " from the toolbar");
			li.append(ic, name, up, down, off);
			ol.append(li);
		});
		wrap.append(ol);
		const again = keep && wrap.querySelector('[data-k="' + keep + '"]:not([disabled])');
		if (again) again.focus();
	};
	render();
	subscribe((_s, d) => { if (!d || !d.keys || d.keys.some((k) => /^ext/.test(k))) render(); });
	return wrap;
}

// An extension's switch, plus "Show in toolbar" for the ones that can have a button.
function extControl(key) {
	const x = EXTENSION_BY_KEY[key];
	const sw = toggle(key);
	if (!x || !canPin(x.id)) return sw;
	const wrap = el("div", "rv-extctl");
	const pin = el("button", "rv-pin");
	pin.type = "button";
	pin.setAttribute("aria-label", "Show " + x.label + " in the toolbar");
	pin.innerHTML = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round" aria-hidden="true"><path d="M9.5 1.5L6 5 3 6l3 3-3.5 3.5 1 1L10 10l3 3 1-1-1-3 3.5-3.5z"/></svg><span>In toolbar</span>';
	const paint = () => { const on = isPinned(x.id, get); pin.setAttribute("aria-pressed", String(on)); pin.title = on ? "Shown in the toolbar while it's on" : "Not in the toolbar"; };
	pin.addEventListener("click", () => setMany(pinPatch(x.id, !isPinned(x.id, get), get)));
	paint();
	subscribe(paint);
	wrap.append(pin, sw);
	return wrap;
}

// Voices for Read Aloud: the ones on this device first, matching the page language.
function voiceControl() {
	const s = el("select", "rv-select");
	const paint = () => {
		const voices = typeof speechSynthesis !== "undefined" ? speechSynthesis.getVoices() : [];
		const list = voiceList(voices, { lang: navigator.language || "en", online: get("readOnline") === true });
		s.textContent = "";
		const best = el("option", "", "Best voice on this device");
		best.value = "";
		s.append(best);
		for (const v of list) { const o = el("option", "", v.name + (v.localService ? "" : " (online)")); o.value = v.name; s.append(o); }
		const cur = String(get("readVoice") || "");
		if (cur && !list.some((v) => v.name === cur)) { const o = el("option", "", cur + " (not available)"); o.value = cur; s.append(o); }
		s.value = cur;
	};
	s.addEventListener("change", () => set("readVoice", s.value));
	paint();
	try { speechSynthesis.addEventListener("voiceschanged", paint); } catch { /* no speech here */ }
	subscribe((_x, d) => { if (!d || !d.keys || d.keys.some((k) => k === "readVoice" || k === "readOnline")) paint(); });
	return s;
}

// High Contrast as one switch over the theme. Turning it on keeps the look you had, so turning it
// off puts that look back (or the default look when there is none).
const PREV_LOOK = "revault:prev-look";
function highContrastControl() {
	const b = el("button", "rv-switch");
	b.type = "button";
	b.setAttribute("role", "switch");
	const hc = BUNDLES.find((x) => x.id === "high-contrast");
	const sync = () => { const v = get("theme") === "contrast"; b.dataset.on = v ? "1" : "0"; b.setAttribute("aria-checked", String(v)); };
	b.addEventListener("click", () => {
		if (get("theme") !== "contrast") {
			const prev = {};
			for (const k of BUNDLE_KEYS) prev[k] = get(k);
			try { localStorage.setItem(PREV_LOOK, JSON.stringify(prev)); } catch { /* storage off */ }
			setMany(hc.settings);
		} else {
			let prev = null;
			try { prev = JSON.parse(localStorage.getItem(PREV_LOOK) || "null"); } catch { prev = null; }
			if (!prev || typeof prev !== "object" || prev.theme === "contrast") prev = Object.fromEntries(BUNDLE_KEYS.map((k) => [k, DEFAULTS[k]]));
			const patch = {};
			for (const k of BUNDLE_KEYS) if (Object.prototype.hasOwnProperty.call(prev, k)) patch[k] = prev[k];
			setMany(patch);
		}
		sync();
	});
	sync();
	subscribe(sync);
	return b;
}

// The weekly goal from the This week card (home/main.js GOALS).
const GOALS = [["days", 3, "Study 3 days"], ["fo", 4, "4 focus sessions"], ["fc", 50, "50 cards"], ["td", 10, "10 tasks"]];
function goalControl() {
	const s = el("select", "rv-select");
	const paint = () => {
		const g = get("homeGoal");
		s.textContent = "";
		const opts = [["", "No goal"], ...GOALS.map(([k, n, l]) => [k + ":" + n, l])];
		if (g && g.kind && !GOALS.some(([k, n]) => k === g.kind && n === g.n)) opts.push([g.kind + ":" + g.n, "Your goal (" + g.n + ")"]);
		for (const [v, l] of opts) { const o = el("option", "", l); o.value = v; s.append(o); }
		s.value = g && g.kind ? g.kind + ":" + g.n : "";
	};
	s.addEventListener("change", () => {
		if (!s.value) { set("homeGoal", null); return; }
		const [kind, n] = s.value.split(":");
		set("homeGoal", { kind, n: Number(n) });
	});
	paint();
	subscribe((_x, d) => { if (!d || !d.keys || d.keys.includes("homeGoal")) paint(); });
	return s;
}

// Keys that work across Re:Vault, for the Keyboard group.
const KEYS = [
	["Ctrl+K", "Command palette: pages, tools and settings"], ["/", "Jump to the address bar"], ["Ctrl+L or Alt+D", "Address bar"],
	["Ctrl+T, Ctrl+W", "New tab, close tab"], ["Ctrl+Shift+T", "Reopen a closed tab"], ["Ctrl+Tab", "Next tab"],
	["Ctrl+ +, Ctrl+ -, Ctrl+0", "Zoom the page in, out, back to normal"], ["Ctrl+F", "Find on the page"], ["Ctrl+,", "Settings"],
	["Alt+R", "Read Aloud: start or pause"], ["Alt+Left, Alt+Right", "Back, forward"],
];
function keysControl() {
	const d = el("details", "rv-disclose");
	d.append(el("summary", "", "Show the keys"));
	const dl = el("dl", "rv-keylist");
	for (const [k, v] of KEYS) { const dt = el("dt", ""); dt.append(el("kbd", "", k)); dl.append(dt, el("dd", "", v)); }
	d.append(dl);
	return d;
}

export function buildControls() {
	const wp = wallpaperControls();
	const lx = linuxControls();
	const C = {
		// Look and feel
		looks: looksControl,
		theme: () => groupedSelect("theme", themeGroups(get("theme"))),
		accent: () => color("accent", ACCENT_PRESETS),
		font: fontControl,
		cornerStyle: () => select("cornerStyle", [["sharp", "Sharp"], ["rounded", "Rounded"], ["soft", "Soft"]]),
		density: () => select("density", [["comfortable", "Comfortable"], ["compact", "Compact"]]),
		...wp,
		// Layout and start page
		layout: () => select("layout", [["classic", "Classic"], ["island", "Island"]]),
		islandPosition: () => select("islandPosition", [["top", "Top"], ["bottom", "Bottom"]]),
		islandEffects: () => select("islandEffects", [["auto", "Auto"], ["full", "Full"], ["normal", "Normal"], ["low", "Low"]]),
		showBrand: () => toggle("showBrand"),
		bumpers: () => {
			// The picker, and a note while something keeps branding hidden (shared/brand-quiet.js).
			const box = document.createElement("div");
			const n = note("Hidden while Quick hide, a tab cloak, about:blank or maximum obfuscation is on, or Show the Re:Vault name is off.", "rv-bumper-quiet");
			const sync = () => { n.hidden = get("bumpers") === "off" || !brandQuiet(get); };
			sync();
			subscribe(sync);
			box.append(select("bumpers", [["visit", "Every visit"], ["day", "One a day"], ["pinned", "Pinned"], ["off", "Off"]]), n);
			return box;
		},
		bumperPin: () => {
			// What could show today, plus the current pin. A pin out of its season stays chosen but
			// waits: the start page rotates as usual until the season opens.
			const T = __RVM.bumperTable, now = Date.now(), pin = get("bumperPin");
			const list = T.filter((b) => b.id === pin || showable(b, now));
			const box = document.createElement("div");
			const n = note("", "rv-bumper-wait");
			const day = (t) => new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "America/New_York" });
			const sync = () => {
				const w = pinWaiting(T, get("bumperPin"), Date.now());
				n.hidden = !w;
				if (!w) return;
				const what = w.season ? "its season, " + w.label : "its day";
				n.textContent = (w.over ? "Waiting for " + what + ", which is over for now." : "Waiting for " + what + ", from " + day(w.from) + ".") + " Until then you get a different one each visit.";
			};
			sync();
			subscribe(sync);
			box.append(select("bumperPin", [["", "Choose one"], ...list.map((b) => [b.id, b.title])]), n);
			return box;
		},
		showLoader: () => toggle("showLoader"),
		showStatus: () => toggle("showStatus"),
		bookmarksBar: () => toggle("bookmarksBar"),
		extButtons: extButtonsControl,
		greetingName: () => textInput("greetingName", "your name"),
		homeTouches: () => toggle("homeTouches"),
		clockShow: () => toggle("clockShow"),
		clock24h: () => toggle("clock24h"),
		clockSeconds: () => toggle("clockSeconds"),
		showShortcuts: () => toggle("showShortcuts"),
		scList: shortcutsControl,
		showRecents: () => toggle("showRecents"),
		showContinue: () => toggle("showContinue"),
		homeWeek: () => toggle("homeWeek"),
		homeTip: () => toggle("homeTip"),
		homeOnline: () => toggle("homeOnline"),
		studyingNow: () => toggle("studyingNow"),
		// Browsing and tabs
		searchEngine: () => select("searchEngine", [["duckduckgo", "DuckDuckGo"], ["google", "Google"], ["bing", "Bing"], ["custom", "Custom…"]]),
		customSearch: () => textInput("customSearch", "https://duckduckgo.com/?q=%s"),
		suggestions: () => toggle("suggestions"),
		paletteEnabled: () => toggle("paletteEnabled"),
		extHelper: () => {
			const t = toggle("extHelper");
			let flag = "off";
			try { flag = localStorage.getItem("revault:helper:flag") || "off"; } catch { /* storage off */ }
			if (flag === "off") t.dataset.empty = "1";
			return t;
		},
		newTab: () => select("newTab", [["start", "Start page"], ["blank", "Blank"], ["url", "A site"]]),
		newTabUrl: () => textInput("newTabUrl", "https://…"),
		homepage: () => textInput("homepage", "https://…"),
		defaultZoom: () => select("defaultZoom", [["50", "50%"], ["75", "75%"], ["90", "90%"], ["100", "100%"], ["110", "110%"], ["125", "125%"], ["150", "150%"], ["175", "175%"], ["200", "200%"]]),
		sessionRestore: () => toggle("sessionRestore"),
		maxLiveTabs: () => select("maxLiveTabs", [["3", "3"], ["4", "4"], ["6", "6"], ["8", "8"], ["10", "10"], ["15", "15"]]),
		warnClose: () => toggle("confirmCloseMany"),
		// Study and focus
		...bellControls,
		...musicControls,
		focusSound: () => select("focusSound", [["off", "No sound"], ...MUSIC_PRESETS.map((m) => [m.id, m.label])]),
		focusSync: () => toggle("focusSync"),
		commonsGarden: () => toggle("commonsGarden"),
		streakShow: () => toggle("streakShow"),
		streakSummer: () => toggle("streakSummer"),
		streakSummerDates: summerControl,
		homeGoal: goalControl,
		clearStats: () => button("Clear study stats", () => {
			if (!confirm("Clear your study days, focus time and milestone progress on this device?")) return;
			clearStats();
			clearStreak({ remove: storeRemove });
			clearMilestones();
		}, "rv-btn-danger"),
		// Extensions
		extReadAloud: () => extControl("extReadAloud"),
		readVoice: voiceControl,
		readRate: () => {
			// saved as a number, like the player's own speed menu
			const s = el("select", "rv-select");
			for (const r of RATES) { const o = el("option", "", r + "x"); o.value = String(r); s.append(o); }
			const sync = () => { s.value = String(Number(get("readRate")) || 1); };
			s.addEventListener("change", () => set("readRate", Number(s.value)));
			sync();
			subscribe(sync);
			return s;
		},
		readMainOnly: () => toggle("readMainOnly"),
		readHighlight: () => toggle("readHighlight"),
		readFollow: () => toggle("readFollow"),
		readRemember: () => toggle("readRemember"),
		readOnline: () => toggle("readOnline"),
		extHighlighter: () => extControl("extHighlighter"),
		extDarkMode: () => extControl("extDarkMode"),
		extPip: () => extControl("extPip"),
		extAdblock: () => extControl("extAdblock"),
		extBandwidth: () => extControl("extBandwidth"),
		extDevtools: () => extControl("extDevtools"),
		// Privacy and safety
		privacyCard: privacyInfo,
		incognito: () => toggle("incognito"),
		adsEnabled: () => toggle("adsEnabled"),
		keepHistory: () => toggle("keepHistory"),
		cloakPreset: () => select("cloakPreset", Object.entries(CLOAK_PRESETS).map(([k, v]) => [k, v.label || k])),
		cloakTitle: () => textInput("cloakTitle", "My Drive - Google Drive"),
		cloakFavicon: () => textInput("cloakFavicon", "https://…/favicon.ico"),
		cloakOnBlur: () => toggle("cloakOnBlur"),
		panicEnabled: () => toggle("panicEnabled"),
		panicKey: () => textInput("panicKey", "`"),
		panicUrl: () => textInput("panicUrl", "https://classroom.google.com"),
		obfuscation: () => select("obfuscation", [["off", "Off"], ["standard", "Standard"], ["max", "Also on screen"]]),
		leaveConfirm: () => toggle("antiClose"),
		aboutBlank: () => toggle("aboutBlank"),
		...privacyActions,
		// Accessibility
		highContrast: highContrastControl,
		animations: () => toggle("animations"),
		performanceMode: () => select("performanceMode", [["auto", "Auto (recommended)"], ["on", "Always on"], ["off", "Off"]]),
		slashShortcut: () => toggle("slashShortcut"),
		keysList: keysControl,
		// Account and backup
		accountBlock,
		supporter: supporterBlock,
		betaEntry: betaEntryControl,
		shareRevault: () => button("Share Re:Vault", (e) => openShareSheet({ from: e.currentTarget })),
		dataHere: () => dataHere({ carry: true }),
		carryCode: carryRow,
		...backupActions,
		// Advanced
		...proxyControls,
		autoUpdate: () => toggle("autoUpdate"),
		updateNotify: () => toggle("updateNotify"),
		whatsNew: () => toggle("whatsNew"),
		installPrompt: () => toggle("installPrompt"),
		...lx,
		...troubleActions,
	};
	return C;
}
